import { useCallback, useEffect, useMemo, useState } from 'react';
import { TaxonomyWrapper } from './TaxonomyWrapper';
import { Typography, SearchInput } from 'app/components/ui';
import { usePresentationTree } from 'app/api/tag-queries/taxonomy';
import { PresentationTreeNetwork, TaxonomyChild, TagWithMetadata } from 'app/api/tag-queries/types';
import { useExtensions, useDocumentExtensionSchema, useDocumentTaxonomyConfig } from 'app/api/tag-queries';
import Spinner from 'app/components/Spinner';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { i18n } from 'app/utils/i18n';
import { TaxonomyFooter } from '../Footer/TaxonomyFooter';
import { useTaxonomySearch } from './useTaxonomySearch';
import { filterVisibleNetworks } from './filterVisibleNetworks';

import './Taxonomy.scss';
import { findAncestorPath, findNodeById, mergeExtensionsIntoPresentationTree } from 'app/utils/tag-utils';

const EMPTY_SET = new Set<string>();

interface TaxonomyProps {
  selectedTagId: string | null;
  isContentMarked: boolean;
  usedTags: string[];
  documentTags: TagWithMetadata[];
  onSelectionChange: (tag: TaxonomyChild | null) => void;
  onDetailsClick?: (tag: TaxonomyChild) => void;
  onExtensionClick?: (tagId: string) => void;
  onHiddenTagCreated?: (tag: unknown) => void;
  hideExtensions?: boolean;
  hideFooter?: boolean;
}

export const Taxonomy = ({
  selectedTagId,
  isContentMarked,
  usedTags,
  documentTags,
  onSelectionChange,
  onDetailsClick,
  onExtensionClick,
  onHiddenTagCreated,
  hideExtensions,
  hideFooter,
}: TaxonomyProps) => {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { superId: documentSuperId } = useEditorSelector((editor: any) => editor?.pDocument);

  const { data: rawTaxonomyData, isLoading, isError } = usePresentationTree(documentSuperId);

  const { data: taxonomyConfig } = useDocumentTaxonomyConfig(documentSuperId);
  const activeTaxonomyId = taxonomyConfig?.taxonomySelections?.find((t) => t.isActive)?.taxonomyId ?? taxonomyConfig?.taxonomySelections?.[0]?.taxonomyId;
  const { data: extensions } = useExtensions(documentSuperId, activeTaxonomyId);
  const { data: extensionSchema } = useDocumentExtensionSchema(documentSuperId);

  const { taxonomyData, extensionConceptNames } = useMemo(
    () => mergeExtensionsIntoPresentationTree(rawTaxonomyData, hideExtensions ? [] : extensions, extensionSchema?.prefix ?? null),
    [rawTaxonomyData, extensions, extensionSchema?.prefix, hideExtensions]
  );

  const [selectedTag, setSelectedTag] = useState<TaxonomyChild | null>(null);
  const [selectedNodePath, setSelectedNodePath] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedTagId || !taxonomyData?.presentationTree) {
      setSelectedTag(null);
      setSelectedNodePath(null);
      return;
    }
    if (selectedTag?.id === selectedTagId) {
      return;
    }
    const networks = taxonomyData.presentationTree.networks ?? [];
    for (const network of networks) {
      for (const root of network.roots) {
        if (root.id === selectedTagId) {
          setSelectedTag({ ...root, order: 0 } as TaxonomyChild);
          setSelectedNodePath(null);
          return;
        }
        const found = findNodeById(root.children, selectedTagId);
        if (found) {
          setSelectedTag(found);
          setSelectedNodePath(null);
          return;
        }
      }
    }
    setSelectedTag(null);
    setSelectedNodePath(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTagId, taxonomyData]);

  const handleSelectTag = useCallback(
    (tag: TaxonomyChild, nodePath?: string) => {
      if (selectedTag?.id === tag.id) {
        if ((nodePath ?? null) === selectedNodePath) {
          setSelectedTag(null);
          setSelectedNodePath(null);
          onSelectionChange(null);
        } else {
          setSelectedNodePath(nodePath ?? null);
        }
        return;
      }
      setSelectedTag(tag);
      setSelectedNodePath(nodePath ?? null);
      onSelectionChange(tag);
    },
    [selectedTag, selectedNodePath, onSelectionChange]
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const allNetworks = taxonomyData?.presentationTree?.networks;
  const linkroleOverrides = useMemo(() => {
    const overrides = (taxonomyConfig as any)?.taxonomyOverrides;
    if (!overrides || !activeTaxonomyId) return undefined;
    return overrides[activeTaxonomyId] as Record<string, { alias?: string; hidden?: boolean }> | undefined;
  }, [taxonomyConfig, activeTaxonomyId]);
  const hiddenURIs = useMemo(() => {
    if (!linkroleOverrides) return new Set<string>();
    const set = new Set<string>();
    for (const [uri, ov] of Object.entries(linkroleOverrides)) {
      if (ov?.hidden === true) set.add(uri);
    }
    return set;
  }, [linkroleOverrides]);
  const aliasByURI = useMemo(() => {
    const map = new Map<string, string>();
    if (!linkroleOverrides) return map;
    for (const [uri, ov] of Object.entries(linkroleOverrides)) {
      if (typeof ov?.alias === 'string' && ov.alias.length > 0) map.set(uri, ov.alias);
    }
    return map;
  }, [linkroleOverrides]);
  const networks = useMemo(() => (allNetworks ? filterVisibleNetworks(allNetworks, hiddenURIs) : allNetworks), [allNetworks, hiddenURIs]);
  const { filteredNetworkIndices, searchExpandIdsByNetwork, searchVisibleIdsByNetwork, isSearching } = useTaxonomySearch(networks, debouncedSearch);

  const [expandToTagId] = useState(selectedTagId);

  const expandTarget = useMemo<{ network: object; ancestorIds: Set<string> } | null>(() => {
    if (!expandToTagId || !taxonomyData?.presentationTree) return null;
    const networks = taxonomyData.presentationTree.networks ?? [];
    for (const network of networks) {
      const root = network.roots[0];
      if (root.id === expandToTagId) return { network, ancestorIds: EMPTY_SET };

      const path = findAncestorPath(root.children, expandToTagId);
      if (path !== null) return { network, ancestorIds: new Set(path) };
    }
    return null;
  }, [expandToTagId, taxonomyData]);

  if (isLoading) {
    return (
      <section className="d-flex justify-content-center py-4">
        <Spinner />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="px-3 py-4">
        <Typography>{i18n('taxonomy.load.error')}</Typography>
      </section>
    );
  }

  if (!taxonomyData || !taxonomyData.presentationTree) {
    return (
      <section className="px-3 py-4">
        <Typography>{i18n('taxonomy.no.imported')}</Typography>
      </section>
    );
  }

  return (
    <>
      <div className="p-3 taxonomy__search-bar">
        <SearchInput searchInput={searchInput} setSearchInput={setSearchInput} />
      </div>

      <section className="taxonomy__scroll-region" onMouseDown={(e) => e.preventDefault()}>
        <div className="px-3">
          {((networks ?? []) as PresentationTreeNetwork[])
            .map((tax, idx) => ({ tax, idx }))
            .filter(({ idx }) => !isSearching || filteredNetworkIndices?.has(idx))
            .map(({ tax, idx }) => (
              <TaxonomyWrapper
                key={idx}
                {...tax}
                selectedTagId={selectedTag?.id ?? null}
                selectedNodePath={selectedNodePath}
                expandToIds={!isSearching && expandTarget?.network === tax ? expandTarget.ancestorIds : null}
                searchExpandIds={isSearching ? searchExpandIdsByNetwork?.get(idx) ?? null : null}
                searchVisibleIds={isSearching ? searchVisibleIdsByNetwork?.get(idx) ?? null : null}
                isSearching={isSearching}
                onSelectTag={handleSelectTag}
                onDetailsClick={onDetailsClick}
                onExtensionClick={onExtensionClick}
                usedTags={usedTags}
                extensionTags={extensionConceptNames}
                aliasOverride={aliasByURI.get(tax.linkrole?.roleURI)}
              />
            ))}

          {isSearching && filteredNetworkIndices?.size === 0 && <Typography>{i18n('taxonomy.no.results')}</Typography>}
          {!isSearching && (networks ?? []).length === 0 && <Typography>{i18n('taxonomy.no.results')}</Typography>}
        </div>
      </section>

      {!hideFooter && (
        <TaxonomyFooter
          selectedTag={selectedTag}
          selectedLinkroleUri={selectedNodePath ? selectedNodePath.split('>')[0] : null}
          isContentMarked={isContentMarked}
          isTagUsed={usedTags?.includes(selectedTag?.id ?? '')}
          documentTags={documentTags}
          onExtensionClick={onExtensionClick}
          onHiddenTagCreated={onHiddenTagCreated}
        />
      )}
    </>
  );
};
